import {
  AloftPrice,
  AloftPriceJSON,
  SubjectData,
  orUndefined,
  schAloftPrice,
} from '@aloft/toolkit-shared';
import { Block, ProductColor, Typography, theme } from '@aloft/toolkit-sui';

import React, { useState } from 'react';
import { Controller, ControllerRenderProps, FieldPath, useWatch } from 'react-hook-form';

import { useAppDispatch } from '../../../redux/hooks.ts';
import { PropertyGridForm } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { readOnlyFieldTypographyProps } from './consts.ts';
import { PropertyGridFieldProps } from './types.ts';

const UnitRatePlaceholderField = ({ colSpan }: { colSpan: number }) => (
  <Typography
    {...readOnlyFieldTypographyProps}
    __dangerDoNotUseSx={{
      ...readOnlyFieldTypographyProps.__dangerDoNotUseSx,
      gridColumn: `span ${colSpan}`,
    }}
  >
    -
  </Typography>
);

const EditableUnitRateFieldInner = ({
  columnIndex,
  field,
  control,
  getValues,
  colSpan = 1,
}: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();
  const { unitRate } = field;
  const { label, key: unitRateKey } = unitRate!;

  const propertyId = useWatch({ control, name: `columns.${columnIndex}.propertyId` });
  const unitRateFieldName =
    `columns.${columnIndex}.propertyData.editableData.salesComparison.unitRates.${unitRateKey}` as FieldPath<PropertyGridForm>;

  const storedUnitRate = useWatch({ control, name: unitRateFieldName }) as
    | AloftPriceJSON
    | undefined;

  const [rawInputValue, setRawInputValue] = useState<string | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);

  const handleSetFieldValue = (onChange: ControllerRenderProps['onChange'], newValue?: string) => {
    const parsedRate = newValue ? AloftPrice.fromDollarLike(newValue) : undefined;
    onChange(parsedRate?.toJSON());

    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    const subjectPropertyData = propertyData as SubjectData | undefined;
    if (subjectPropertyData?.editableData) {
      if (!subjectPropertyData.editableData.salesComparison) {
        subjectPropertyData.editableData.salesComparison = { unitRates: {} };
      } else if (!subjectPropertyData.editableData.salesComparison.unitRates) {
        subjectPropertyData.editableData.salesComparison.unitRates = {};
      }
      subjectPropertyData.editableData.salesComparison.unitRates![unitRateKey] =
        parsedRate?.toJSON();
    }
    handleUpdatePropertyData(propertyId, getValues(), dispatch, subjectPropertyData);
  };

  const displayRate = orUndefined(schAloftPrice.parse)(storedUnitRate);

  return (
    <Controller
      name={unitRateFieldName}
      control={control}
      render={({ field: { onChange, ref } }) => {
        const rateString = displayRate?.toString(true);

        const handleFocus = () => {
          setIsFocused(true);
          const raw = rateString?.replace(/-?\$/, (m) => (m === '-$' ? '-' : '')) ?? '';
          setRawInputValue(raw);
        };

        const handleBlur = () => {
          setIsFocused(false);
          const inputToParse = (rawInputValue ?? rateString ?? '').trim();
          try {
            if (
              !inputToParse ||
              inputToParse === '-' ||
              AloftPrice.fromDollarLike(inputToParse).equals(AloftPrice.zero)
            ) {
              handleSetFieldValue(onChange, undefined);
            } else {
              handleSetFieldValue(onChange, inputToParse);
            }
          } catch {
            handleSetFieldValue(onChange, rateString);
          }
          setRawInputValue(undefined);
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
          if (e.key === 'Escape') {
            setRawInputValue(undefined);
            setIsFocused(false);
            onChange(storedUnitRate ?? undefined);
          } else if (e.key === 'Enter' || e.key === 'NumpadEnter') {
            (e.target as HTMLInputElement).blur();
          }
        };

        const inputValue = (() => {
          const v = isFocused && rawInputValue !== undefined ? rawInputValue : (rateString ?? '');
          return v.replace(/^(-?)\$/, '$1');
        })();

        const sharedLabelStyle: React.CSSProperties = {
          fontSize: '12px',
          lineHeight: '1',
          color: theme.colors.textSecondary,
          whiteSpace: 'nowrap',
          userSelect: 'none',
          flexShrink: 0,
        };

        const hasValue = displayRate && !displayRate.equals(AloftPrice.zero);
        const borderStyle = hasValue
          ? `1px dashed ${theme.colors.buttonDefault}`
          : '1px solid transparent';

        return (
          <Block
            __dangerDoNotUseSx={{
              width: '100%',
              height: '100%',
              gridColumn: `span ${colSpan}`,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: theme.colors.textOffset,
              border: borderStyle,
              outline: 'none',
              cursor: 'text',
              _focusWithin: {
                outline: '1.5px solid',
                outlineOffset: '-1.5px',
                outlineColor: theme.colors.buttonDefault,
                background: theme.colors.surface3,
                border: '1px solid transparent',
              },
              _hover: {
                outline: '1.5px solid',
                outlineOffset: '-1.5px',
                outlineColor: theme.colors.buttonDefault,
              },
            }}
          >
            <span style={{ ...sharedLabelStyle, paddingLeft: '6px' }}>$</span>
            <input
              ref={ref}
              type="text"
              value={inputValue}
              placeholder="-"
              style={{
                flex: 1,
                minWidth: 0,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: '12px',
                lineHeight: '1',
                color: displayRate?.getDisplayColor() as ProductColor | undefined,
                padding: '0 2px',
                textAlign: 'left',
              }}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={(e) => setRawInputValue(e.target.value.replace(/^0+(?=\d)/, ''))}
              onKeyDown={handleKeyDown}
            />
            <span style={{ ...sharedLabelStyle, paddingRight: '6px' }}>{label}</span>
          </Block>
        );
      }}
    />
  );
};

export const EditableUnitRateField = (props: PropertyGridFieldProps) => {
  const { field, colSpan = 1 } = props;
  const { unitRate } = field;

  if (!unitRate) return <UnitRatePlaceholderField colSpan={colSpan} />;
  return <EditableUnitRateFieldInner {...props} />;
};
